import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import '../../styles/midnight-lab.css';

function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut, loading } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    if (!user && !loading) {
      navigate('/dashboard/login', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--color-bg-primary)'
      }}>
        <div style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-secondary)'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const navItems = [
    { path: '/dashboard', label: 'Home', icon: 'Home' },
    { path: '/dashboard/inventory', label: 'Inventory', icon: 'Package' },
    { path: '/dashboard/tasks', label: 'Tasks', icon: 'CheckSquare' },
    { path: '/dashboard/calendar', label: 'Calendar', icon: 'Calendar' },
    { path: '/dashboard/roles', label: 'Roles', icon: 'Users' },
    { path: '/dashboard/settings', label: 'Settings', icon: 'Settings' }
  ];

  const isActive = (path) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      backgroundColor: 'var(--color-bg-primary)',
      paddingTop: '80px'
    }}>
      {/* Sidebar */}
      <aside style={{
        width: sidebarOpen ? '280px' : '80px',
        backgroundColor: 'var(--color-bg-primary)',
        borderRight: '1px solid rgba(0, 16, 17, 0.1)',
        padding: 'var(--space-6)',
        transition: 'width 0.3s ease',
        position: 'fixed',
        height: 'calc(100vh - 80px)',
        overflowY: 'auto',
        zIndex: 100
      }}>
        <div style={{
          marginBottom: 'var(--space-8)',
          paddingBottom: 'var(--space-6)',
          borderBottom: '1px solid rgba(0, 16, 17, 0.1)'
        }}>
          <h2 style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xl)',
            fontWeight: 700,
            color: 'var(--color-text-primary)',
            opacity: sidebarOpen ? 1 : 0,
            transition: 'opacity 0.3s ease',
            whiteSpace: 'nowrap',
            overflow: 'hidden'
          }}>
            Dashboard
          </h2>
          {sidebarOpen && user && (
            <div style={{
              marginTop: 'var(--space-4)',
              paddingTop: 'var(--space-4)',
              borderTop: '1px solid rgba(0, 16, 17, 0.1)'
            }}>
              <div style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-primary)',
                marginBottom: 'var(--space-1)'
              }}>
                {user.name}
              </div>
              <div style={{
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-xs)',
                color: 'var(--color-text-secondary)',
                textTransform: 'capitalize',
                marginBottom: 'var(--space-3)'
              }}>
                {user.role}
              </div>
              <button
                onClick={() => {
                  signOut();
                  navigate('/dashboard/login');
                }}
                style={{
                  width: '100%',
                  padding: 'var(--space-2) var(--space-3)',
                  backgroundColor: 'transparent',
                  color: 'var(--color-text-secondary)',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xs)',
                  border: '1px solid rgba(0, 16, 17, 0.1)',
                  cursor: 'pointer',
                  transition: 'all var(--transition-base)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.borderColor = 'var(--color-frozen-lake)';
                  e.target.style.color = 'var(--color-text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.borderColor = 'rgba(0, 16, 17, 0.1)';
                  e.target.style.color = 'var(--color-text-secondary)';
                }}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>

        <nav style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-2)'
        }}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-3)',
                padding: 'var(--space-3) var(--space-4)',
                textDecoration: 'none',
                color: isActive(item.path) ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                backgroundColor: isActive(item.path) ? 'var(--color-bg-secondary)' : 'transparent',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                fontWeight: isActive(item.path) ? 600 : 400,
                transition: 'all var(--transition-base)',
                borderRadius: '6px',
                borderLeft: isActive(item.path) ? '3px solid var(--color-frozen-lake)' : '3px solid transparent'
              }}
              onMouseEnter={(e) => {
                if (!isActive(item.path)) {
                  e.target.style.backgroundColor = 'var(--color-bg-secondary)';
                }
              }}
              onMouseLeave={(e) => {
                if (!isActive(item.path)) {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div style={{
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                {getIcon(item.icon)}
              </div>
              <span style={{
                opacity: sidebarOpen ? 1 : 0,
                transition: 'opacity 0.3s ease',
                whiteSpace: 'nowrap',
                overflow: 'hidden'
              }}>
                {item.label}
              </span>
            </Link>
          ))}
        </nav>

        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          style={{
            position: 'absolute',
            bottom: 'var(--space-6)',
            left: sidebarOpen ? 'var(--space-6)' : '50%',
            transform: sidebarOpen ? 'none' : 'translateX(-50%)',
            width: '40px',
            height: '40px',
            borderRadius: '6px',
            border: '1px solid rgba(0, 16, 17, 0.1)',
            backgroundColor: 'var(--color-bg-primary)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease'
          }}
        >
          <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
          </svg>
        </button>
      </aside>

      {/* Main Content */}
      <main style={{
        flex: 1,
        marginLeft: sidebarOpen ? '280px' : '80px',
        transition: 'margin-left 0.3s ease',
        padding: 'var(--space-8)',
        maxWidth: sidebarOpen ? 'calc(100vw - 280px)' : 'calc(100vw - 80px)',
        overflowX: 'auto'
      }}>
        <Outlet />
      </main>
    </div>
  );
}

function getIcon(iconName) {
  const icons = {
    Home: (
      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
    Package: (
      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
      </svg>
    ),
    CheckSquare: (
      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
      </svg>
    ),
    Calendar: (
      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    Users: (
      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    ),
    Settings: (
      <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )
  };
  return icons[iconName] || null;
}

export default DashboardLayout;

